import React from 'react';
import ReactDOM from 'react-dom';
import store from '../../../store';
import DrawingOptions from './DrawingOptions';
import DrawingTools from './DrawingTools';
import GeoWorkWindow from "./GeoWorkWindow";
import StateProvider from "../control/StateProvider";
import ShapePopup from './ShapePopup';
import { GeoFileImporter } from './GeoFileImporter';

export default class DrawingControl extends DrawingTools {
    constructor(map, google) {
        super(map, google);
        this.mapMarker = null;
        this.controlBox = null;
        this.controlName = "drawingtools";
        this.drawingComponent = null;
        this.replayBox = null;
        this.workWindow = new GeoWorkWindow(map, google, this);

        this.farmId = null;
        this.regionsList = [];
        this.refresh = 0;

        this.close = this.close.bind(this);
        this.discard = this.discard.bind(this);
        this.setMapMarker = this.setMapMarker.bind(this);

        this._addEventListener();
        this.storeSubscribe();
        super.usePlugin("file", new GeoFileImporter());
    }

    setMapMarker(markerObj) {
        this.mapMarker = markerObj;
        return this;
    }

    setReplay(replayObj) {
        this.replayBox = replayObj;
        return this;
    }

    storeSubscribe() {
        this.storeUnsubscribe = store.subscribe(() => {
            const state = store.getState();
            const current = state.geo.regionsList.map(e => e.geo_region_uuid).sort();
            const previous = this.regionsList.map(e => e.geo_region_uuid).sort();
            if (current.join() !== previous.join() || this.refresh < state.geo.regionRefresh) {
                this.farmId = state.geo.farmId;
                this.regionsList = state.geo.regionsList;
                this.refresh = state.geo.regionRefresh;
                this.bindShapeGroup();
                this.showShapeGroups(state.geo.selectedRegionIds, true);
                this.emit("shapes_loaded");
            }
        });
    }

    open(title) {
        if (!this.controlBox) this.controlBox = this.map.getControlBox(this.google.maps.ControlPosition.TOP_CENTER);

        if (this.controlBox.getControl(this.controlName)) {
            if (this.drawingComponent) this.drawingComponent.current.setTitle(title);
            return this;
        }

        this.controlBox.removeAll();
        this.drawingComponent = React.createRef();
        this.controlBox.add({
            name: this.controlName, className: "map-drawing-control", index: 2,
            component: (<DrawingOptions
                ref={this.drawingComponent}
                drawingTools={this}
                onClose={this.close}
                onDiscard={this.discard}
                isReplayOpen={this.replayBox.isOpen()}
            />),
            onLoaded: () => this.drawingComponent.current.setTitle(title)
        });

        if (this.replayBox.isOpen()) this.close();
        else this.mapMarker && this.mapMarker.hideMarkers();

        return this;
    }

    close() {
        this.mapMarker && this.mapMarker.showMarkers();
        super.discard().reset();
        this._clearControl();
    }

    dispose() {
        this.workWindow.dispose();
        super.dispose();
        this.close();
        if (this.storeUnsubscribe) this.storeUnsubscribe();
        return this;
    }

    acceptChanges() {
        super.closeShapeInfowindow().reset();
        this._clearControl();
    }

    bindShapeGroup() {
        this.clearShapes();
        const geoShapeGroup = {};
        for (const region of this.regionsList) {
            const shapes = {};

            for (const shape of region.shapes) {
                shapes[shape.geo_shape_object_uuid] = {
                    type: shape.geo_shape_type,
                    location: shape.location,
                    properties: shape.properties,
                    shapeRefId: shape.geo_shape_uuid,
                    shapeGroupId: region.geo_region_uuid,
                    name: shape.geo_shape_name,
                    tareFactor: shape.tare_factor,
                    workStatus: shape.work_status
                }
            }

            geoShapeGroup[region.geo_region_uuid] = {
                name: region.geo_region_name,
                cropName: region.crop_name,
                customerName: region.customer_name,
                type: region.geo_region_type_id,
                visible: this.shapeGroups[region.geo_region_uuid] ? this.shapeGroups[region.geo_region_uuid].visible : region.active,
                isPreLoaded: true,
                variety: region.variety,
                shapes
            }
        }

        super.drawShapeGroups(geoShapeGroup);
        return this;
    }

    _clearControl() {
        if (!this.controlBox) return this;
        this.controlBox.remove(this.controlName);
        return this;
    }

    _addEventListener() {
        const me = this;
        this.addListener("shape_infowindow_open", null, ({ popupElement, data }) => {
            ReactDOM.render(
                <StateProvider>
                    <ShapePopup
                        drawingTool={this}
                        shape={{ ...data, farmId: me.farmId }}
                        onEdit={({ id, name, tare_factor }) => me.editShapeDetail(id, { name, tare_factor })}
                    />
                </StateProvider>,
                popupElement);
        });

        this.addListener("shape_infowindow_close", null, ({ popupElement }) => {
            if (popupElement) ReactDOM.unmountComponentAtNode(popupElement);
        });
    }
}

export const getDrawingInstance = () => {
    return window.__drawingControl ? window.__drawingControl : null;
}

export const setDrawingInstance = instance => {
    window.__drawingControl = instance;
}